import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCriminalAppellateWizardContext } from '../../../hooks/useCaseRequestWizard';
import { useWizardMaxLengthChange } from '../useWizardMaxLengthChange';

import { focusFirstInvalid, type RequiredErrors} from '../../../utils/requiredValidation';
import { sliceMax } from '../../../utils/caseFieldValidation';
import { SectionCard } from '../WizardComponents';
import WizardSelect from '../../../components/wizard/WizardSelect';
import DocumentPreviewModal from '../../../components/ui/DocumentPreviewModal';
import type { CriminalDocumentRow } from '../../../types/criminalCaseRequest';
import { CriminalAppellateWizardActions } from './CriminalAppellateWizardActions';
import { criminalAppellateWizardPath, CriminalAppellateWizardShell, FieldError, inpClass } from './CriminalAppellateWizardShell';
import { validateCriminalAppellateStep } from './criminalAppellateStepValidation';
import { useCriminalAppellateLookups } from './useCriminalAppellateLookups';
import { useCaseCategorization } from '../criminal/useCaseCategorization';
import * as criminalAppellateService from '../../../services/criminalAppellateCaseRequests';
import {
  clearCriminalPreviewFile,
  setCriminalPreviewFile } from '../../../utils/criminalDocumentPreviewCache';
import { setFlashToast } from '../../../utils/flashToast';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

const MAX_DESC = 2000;
const MAX_REF = 255;
const JUDGEMENT_PREVIEW_ID = 'capp-judgement-order';

function lookupName(items: { id: string; name: string }[], id: string): string {
  return items.find((x) => x.id === id)?.name ?? '';
}

export default function CriminalAppellateStep1CaseOverviewPage() {
  const f = useWizardFieldLabels();
  const nav = useNavigate();
  const { draft, setDraft } = useCriminalAppellateWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const lookups = useCriminalAppellateLookups('step1', draft.provinceId, draft.districtId);
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId);

  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));
  const maxLen = useWizardMaxLengthChange(setErrors);

  useEffect(() => {
    if (draft.connectedAgdFileRef?.trim() && !draft.connectedAgdFileRefAvailable) {
      setDraft((d) => ({ ...d, connectedAgdFileRefAvailable: true }));
    }
  }, [draft.connectedAgdFileRef, draft.connectedAgdFileRefAvailable, setDraft]);


  const validate = (): boolean => {
    const { valid, errors: nextErrors, specs } = validateCriminalAppellateStep(
      1,
      draft,
      cat.fileCategories,
      lookups.corporateInstitutes,
    );
    setErrors(nextErrors);
    if (!valid) focusFirstInvalid(specs, nextErrors);
    return valid;
  };

  const handleJudgementUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;
    setUploading(true);
    const result = await criminalAppellateService.uploadCaseFile(file);
    setUploading(false);
    if (!result.success) {
      setFlashToast({ message: result.message ?? 'Upload failed.', variant: 'error' });
      return;
    }
    setCriminalPreviewFile(JUDGEMENT_PREVIEW_ID, file);
    setDraft((d) => ({
      ...d,
      judgementOrderFileName: result.data.file_name,
      judgementOrderOriginalFileName: result.data.original_file_name ?? file.name,
      judgementOrderFileSize: result.data.file_size }));
    clearErr('judgementOrderFileName');
  };

  const removeJudgement = () => {
    clearCriminalPreviewFile(JUDGEMENT_PREVIEW_ID);
    setPreviewOpen(false);
    setDraft((d) => ({
      ...d,
      judgementOrderFileName: '',
      judgementOrderOriginalFileName: '',
      judgementOrderFileSize: undefined }));
  };

  return (
    <CriminalAppellateWizardShell
      currentStep={1}
      draft={draft}
      setDraft={setDraft}
      categorizationEditable
      categorizationErrors={errors}
      onClearCategorizationError={clearErr}
    >
      <SectionCard title={f.sectionCaseIntakeDetails}>
        {/* Description */}
        <div className="ccr-fg" style={{ marginBottom: 14 }}>
          <label>
            {f.description} <span className="req">*</span>
          </label>
          <textarea
            id="capp-description"
            className={inpClass(Boolean(errors.description))}
            rows={4}
            value={draft.description}
            onChange={(e) => {
              setDraft((d) => ({ ...d, description: maxLen('description', e.target.value, MAX_DESC) }));
            }}
            placeholder={f.briefDescriptionOfCase}
          />
          <FieldError message={errors.description} />
        </div>

        {/* Appeal Matter */}
        <div className="ccr-fg" id="capp-appealMatter" style={{ marginBottom: 14 }}>
          <label>
            {f.appealMatter} <span className="req">*</span>
          </label>
          <div className="ccr-radio-row">
            <label className="ccr-radio">
              <input
                type="radio"
                name="capp-appealMatter"
                checked={draft.appealMatter === 'yes'}
                onChange={() => {
                  setDraft((d) => ({ ...d, appealMatter: 'yes' }));
                  clearErr('appealMatter');
                }}
              />
              <span>{f.yes}</span>
            </label>
            <label className="ccr-radio">
              <input
                type="radio"
                name="capp-appealMatter"
                checked={draft.appealMatter === 'no'}
                onChange={() => {
                  clearCriminalPreviewFile(JUDGEMENT_PREVIEW_ID);
                  setPreviewOpen(false);
                  setDraft((d) => ({
                    ...d,
                    appealMatter: 'no',
                    originalCourt: '',
                    judgementOrderFileName: '',
                    judgementOrderOriginalFileName: '',
                    judgementOrderFileSize: undefined }));
                  clearErr('appealMatter');
                  clearErr('originalCourt');
                  clearErr('judgementOrderFileName');
                }}
              />
              <span>{f.no}</span>
            </label>
          </div>
          <FieldError message={errors.appealMatter} />
        </div>

        {/* Original Court + Judgement/Order — shown only when Appeal Matter = Yes */}
        {draft.appealMatter === 'yes' ? (
          <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
            <div className="ccr-fg">
              <label>
                {f.originalCourt} <span className="req">*</span>
              </label>
              <input
                id="capp-originalCourt"
                className={inpClass(Boolean(errors.originalCourt))}
                type="text"
                value={draft.originalCourt}
                onChange={(e) => {
                  setDraft((d) => ({ ...d, originalCourt: maxLen('originalCourt', e.target.value, MAX_REF) }));
                }}
                placeholder={f.enterCourtName}
              />
              <FieldError message={errors.originalCourt} />
            </div>
            <div className="ccr-fg" id="capp-judgementOrder">
              <label>
                {f.judgementOrderShort} <span className="req">*</span>
              </label>
              {draft.judgementOrderFileName ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, wordBreak: 'break-all' }}>
                    {draft.judgementOrderOriginalFileName || draft.judgementOrderFileName}
                  </span>
                  <button
                    className="ccr-btn-ol"
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    aria-label="View Judgement / Order"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                    View
                  </button>
                  <button
                    className="ccr-btn-ol"
                    type="button"
                    onClick={removeJudgement}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <button
                  className="ccr-btn-ol"
                  type="button"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png,.pdf,.xlsx,.docx"
                style={{ display: 'none' }}
                onChange={(e) => void handleJudgementUpload(e)}
              />
              <FieldError message={errors.judgementOrderFileName} />
            </div>
          </div>
        ) : null}

        {/* Province + District */}
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.province} <span className="req">*</span>
            </label>
            <WizardSelect
              id="capp-province"
              hasError={Boolean(errors.provinceId)}
              value={draft.provinceId}
              placeholder={f.selectProvince}
              options={lookups.provinces.map((p) => ({ id: p.id, label: p.name }))}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  provinceId: id,
                  provinceName: lookupName(lookups.provinces, id),
                  districtId: '',
                  districtName: '',
                  policeStationId: '',
                  policeStationName: '' }));
                clearErr('provinceId');
              }}
              onClear={() => {
                setDraft((d) => ({
                  ...d,
                  provinceId: '',
                  provinceName: '',
                  districtId: '',
                  districtName: '',
                  policeStationId: '',
                  policeStationName: '' }));
                clearErr('provinceId');
              }}
            />
            <FieldError message={errors.provinceId} />
          </div>
          <div className="ccr-fg">
            <label>
              {f.district} <span className="req">*</span>
            </label>
            <WizardSelect
              id="capp-district"
              hasError={Boolean(errors.districtId)}
              value={draft.districtId}
              disabled={!draft.provinceId}
              placeholder={f.selectDistrict}
              options={lookups.districts.map((d) => ({ id: d.id, label: d.name }))}
              onChange={(id) => {
                setDraft((d) => ({
                  ...d,
                  districtId: id,
                  districtName: lookupName(lookups.districts, id),
                  policeStationId: '',
                  policeStationName: '' }));
                clearErr('districtId');
              }}
            />
            <FieldError message={errors.districtId} />
          </div>
        </div>

        {/* Police Station */}
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>{f.policeStation}</label>
            <WizardSelect
              id="capp-policeStation"
              value={draft.policeStationId}
              disabled={!draft.districtId}
              placeholder={f.selectPoliceStation}
              options={lookups.policeStations.map((p) => ({ id: p.id, label: p.name }))}
              onChange={(id) =>
                setDraft((d) => ({
                  ...d,
                  policeStationId: id,
                  policeStationName: lookupName(lookups.policeStations, id) }))
              }
            />
          </div>
          <div />
        </div>

        {/* Your Ref + Other External Ref */}
        <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
          <div className="ccr-fg">
            <label>
              {f.yourReferenceNumber} <span className="req">*</span>
            </label>
            <input
              id="capp-yourRef"
              className={inpClass(Boolean(errors.yourReferenceNumber))}
              type="text"
              value={draft.yourReferenceNumber}
              onChange={(e) => {
                setDraft((d) => ({ ...d, yourReferenceNumber: maxLen('yourReferenceNumber', e.target.value, MAX_REF) }));
              }}
              placeholder={f.enterExternalReferenceNumber}
            />
            <FieldError message={errors.yourReferenceNumber} />
          </div>
          <div className="ccr-fg">
            <label>{f.otherExternalReferences}</label>
            <input
              className={inpClass(Boolean(errors.otherExternalReferences))}
              type="text"
              value={draft.otherExternalReferences}
              onChange={(e) =>
                setDraft((d) => ({ ...d, otherExternalReferences: maxLen('otherExternalReferences', e.target.value, MAX_REF) }))
              }
              placeholder={f.commaSeparatedReferenceNumbers}
            />
            <FieldError message={errors.otherExternalReferences} />
          </div>
        </div>

        {/* Case Number + Connected Matters */}
        <div
          className="ccr-fr c2"
          style={{ marginBottom: draft.hasConnectedMatters === 'yes' ? 14 : 0 }}
        >
          <div className="ccr-fg">
            <label>{f.caseNumber}</label>
            <input
              className={inpClass(Boolean(errors.caseNumber))}
              type="text"
              value={draft.caseNumber}
              onChange={(e) =>
                setDraft((d) => ({ ...d, caseNumber: maxLen('caseNumber', e.target.value, MAX_REF) }))
              }
              placeholder={f.courtReferenceNumber}
            />
            <FieldError message={errors.caseNumber} />
          </div>
          <div className="ccr-fg" id="capp-connected">
            <label>
              {f.connectedMatters} <span className="req">*</span>
            </label>
            <div className="ccr-radio-row">
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="capp-connected"
                  checked={draft.hasConnectedMatters === 'yes'}
                  onChange={() => {
                    setDraft((d) => ({ ...d, hasConnectedMatters: 'yes' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.yes}</span>
              </label>
              <label className="ccr-radio">
                <input
                  type="radio"
                  name="capp-connected"
                  checked={draft.hasConnectedMatters === 'no'}
                  onChange={() => {
                    setDraft((d) => ({
                      ...d,
                      hasConnectedMatters: 'no',
                      connectedCaseNumber: '',
                      connectedAgdFileRefAvailable: false,
                      connectedAgdFileRef: '' }));
                    clearErr('hasConnectedMatters');
                  }}
                />
                <span>{f.no}</span>
              </label>
            </div>
            <FieldError message={errors.hasConnectedMatters} />
          </div>
        </div>

        {/* Connected matter fields */}
        {draft.hasConnectedMatters === 'yes' ? (
          <>
            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <label>
                  {f.connectedCaseNumber} <span className="req">*</span>
                </label>
              </div>
              <div className="ccr-fg" style={{ marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <label style={{ marginBottom: 0 }}>
                      {f.connectedAgdFileRef} <span className="req">*</span>
                    </label>
                  <input
                    type="checkbox"
                    style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer', flexShrink: 0 }}
                    checked={draft.connectedAgdFileRefAvailable}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setDraft((d) => ({
                        ...d,
                        connectedAgdFileRefAvailable: checked,
                        connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                      }));
                      clearErr('connectedAgdFileRef');
                    }}
                  />
                  <span>Available</span>
                </div>
              </div>
            </div>

            <div className="ccr-fr c2" style={{ marginBottom: 0 }}>
              <div className="ccr-fg">
                <input
                  id="capp-connectedCase"
                  className={inpClass(Boolean(errors.connectedCaseNumber))}
                  type="text"
                  maxLength={MAX_REF}
                  value={draft.connectedCaseNumber}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedCaseNumber');
                  }}
                  placeholder={f.enterConnectedCaseNumber}
                />
                <FieldError message={errors.connectedCaseNumber} />
              </div>
              <div className="ccr-fg">
                <input
                  id="capp-connectedAgd"
                  className={inpClass(Boolean(errors.connectedAgdFileRef))}
                  type="text"
                  maxLength={MAX_REF}
                  disabled={!draft.connectedAgdFileRefAvailable}
                  style={!draft.connectedAgdFileRefAvailable ? { cursor: 'default', pointerEvents: 'none' } : undefined}
                  value={draft.connectedAgdFileRef}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) }));
                    clearErr('connectedAgdFileRef');
                  }}
                  placeholder={f.enterConnectedAgdFileRef}
                />
                <div className="ccr-slbl">
                  If you are unaware of the relevant Connected AGD File Reference Number details, please leave the
                  'Available' checkbox unchecked.
                </div>
                <FieldError message={errors.connectedAgdFileRef} />
              </div>
            </div>
          </>
        ) : null}
      </SectionCard>

      <DocumentPreviewModal
        isOpen={previewOpen}
        doc={
          {
            id: JUDGEMENT_PREVIEW_ID,
            documentName: 'Judgement / Order',
            fileName: draft.judgementOrderFileName,
            originalFileName: draft.judgementOrderOriginalFileName} as unknown as CriminalDocumentRow
        }
        onClose={() => setPreviewOpen(false)}
      />

      <CriminalAppellateWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={1}
        validateBeforeSave={validate}
        onNext={() => {
          if (validate()) nav(criminalAppellateWizardPath(2, draft.draftRequestId));
        }}
      />
    </CriminalAppellateWizardShell>
  );
}
